import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/features/auth/useAuth';
import { usePermissions } from '@/features/auth/usePermissions';
import { AppLauncher } from '@/features/apps/AppLauncher';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { OrgSwitcher } from '@/features/organizations/OrgSwitcher';

interface NavItem {
  to: string;
  label: string;
  permission: string | null;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Navigation is permission-driven.
 *
 * A link the user could not use is not shown — but the API refuses regardless, so this is
 * presentation, not protection.
 *
 * GROUPED BECAUSE NINE FLAT LINKS DID NOT FIT. They sat in one non-wrapping row beside the wordmark,
 * the organisation switcher, the role chip, the launcher, the bell, the user's email and a sign-out
 * button — crowded on a laptop and overflowing on anything narrower. Four items and an account menu
 * leave room for the things that are not navigation.
 *
 * The grouping follows how the work divides rather than how the API does: Commercial is the pipeline
 * a salesperson lives in, Directory is the reference data behind it, Administration is who may do
 * what. Dashboard stays a plain link because it is where people land.
 */
const DASHBOARD: NavItem = { to: '/', label: 'Dashboard', permission: null };

const GROUPS: NavGroup[] = [
  {
    label: 'Commercial',
    items: [
      { to: '/rfqs', label: 'Requests', permission: 'rfq:view' },
      { to: '/quotations', label: 'Quotations', permission: 'quotation:view' },
      { to: '/approvals', label: 'Approvals', permission: 'approval:view' },
    ],
  },
  {
    label: 'Directory',
    items: [
      { to: '/customers', label: 'Customers', permission: 'customer:view' },
      { to: '/catalogue', label: 'Catalogue', permission: 'product:view' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/members', label: 'Members', permission: 'member:view' },
      { to: '/invitations', label: 'Invitations', permission: 'invitation:view' },
      { to: '/roles', label: 'Roles', permission: 'role:view' },
    ],
  },
];

export function AppShell() {
  const { user, activeRole, logout } = useAuth();
  const { can } = usePermissions();

  // Children are filtered FIRST and a group with none is dropped entirely — otherwise a Viewer is
  // offered an "Administration" menu that opens onto nothing.
  const groups = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.permission === null || can(item.permission)),
  })).filter((group) => group.items.length > 0);

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            Quotient
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

          <OrgSwitcher />

          <Stack direction="row" spacing={0.5} sx={{ ml: 2 }}>
            <Button
              component={NavLink}
              to={DASHBOARD.to}
              end
              size="small"
              color="inherit"
              sx={{ '&.active': { bgcolor: 'action.selected', fontWeight: 700 } }}
            >
              {DASHBOARD.label}
            </Button>

            {groups.map((group) => (
              <NavGroupMenu key={group.label} group={group} />
            ))}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          {activeRole && (
            <Chip size="small" variant="outlined" label={activeRole.replace(/_/g, ' ')} />
          )}

          {/* Top right, before the bell: the launcher is navigation and the bell is work. */}
          <AppLauncher />

          <NotificationBell />

          <AccountMenu email={user?.email} onSignOut={() => void logout()} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Outlet />
      </Container>
    </Box>
  );
}

/**
 * One group of links, behind a menu.
 *
 * THE TRIGGER CARRIES THE ACTIVE STATE, and it has to be computed rather than delegated. `NavLink`'s
 * `.active` class styles a link; this is a button that opens a menu, so without comparing the
 * current path against the group's children the user loses all sense of where they are the moment
 * navigation is nested.
 */
function NavGroupMenu({ group }: { group: NavGroup }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();

  const isActive = group.items.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );

  return (
    <>
      <Button
        size="small"
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        sx={{
          ...(isActive && { bgcolor: 'action.selected', fontWeight: 700 }),
        }}
      >
        {group.label}
      </Button>

      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {group.items.map((item) => (
          // `component={NavLink}` so middle-click, keyboard navigation and the browser's own
          // open-in-new-tab all behave as they would on a link.
          <MenuItem
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={() => setAnchor(null)}
            sx={{ '&.active': { fontWeight: 700 } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The signed-in user, and the two things that belong to them rather than to the organisation.
 *
 * NOTIFICATION SETTINGS WERE UNREACHABLE. `/settings/notifications` has been a registered route with
 * no entry in the navigation, so the only way to a preference page about your own email was to type
 * the URL. It belongs here rather than in a group: it is about you, not about the tenant — the same
 * reason its route carries no permission gate.
 */
function AccountMenu({ email, onSignOut }: { email?: string | undefined; onSignOut: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Button
        size="small"
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label="Account"
        sx={{ maxWidth: 220 }}
      >
        <Typography variant="body2" color="text.secondary" noWrap>
          {email}
        </Typography>
      </Button>

      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem
          component={NavLink}
          to="/settings/notifications"
          onClick={() => setAnchor(null)}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Notification settings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
